/**
 * Session state for one open "Get on the list" (Coming Soon "Get Notified") form
 * (components/getOnTheListForm.js). Module-level singleton — only one lead form is ever mounted
 * at a time (they share the same modal container), so there's never more than one session.
 *
 * Mirrors leadFormTrackingState.js (NHS20-8253) — NHS20-9093's tracking plan reuses Contact
 * Builder's exact event vocabulary and abandonment/validation semantics for this form too, just
 * with its own field set and a fixed 'Get on the list' buttonName (see getOnTheListTracking.js).
 * Not a Redux slice: this data is purely for tracking and never drives UI.
 */
const TRACKED_FIELD_NAMES = [
  'name', 'email', 'phone', 'postalCode', 'preferredContactMethod', 'realtorQuestion',
];

const state = {
  started: false,
  fromPage: null,
  openedAt: null,
  formStarted: false,
  submitAttempted: false,
  lastFieldInteracted: null,
  submitted: false,
  errorFieldNames: new Set(),
};

export function startNotifySession({ fromPage } = {}) {
  state.started = true;
  state.fromPage = fromPage;
  state.openedAt = Date.now();
  state.formStarted = false;
  state.submitAttempted = false;
  state.lastFieldInteracted = null;
  state.submitted = false;
  state.errorFieldNames = new Set();
}

/**
 * Ignores interactions on fields this flow doesn't track — only a tracked field can become
 * formStarted/lastFieldInteracted, so abandonment/validation analytics never report an untracked
 * field name.
 * @param {string|null|undefined} fieldName
 */
export function recordFieldInteraction(fieldName) {
  if (!fieldName || !TRACKED_FIELD_NAMES.includes(fieldName)) {
    return;
  }

  state.formStarted = true;
  state.lastFieldInteracted = fieldName;
}

export function recordValidationErrors(fieldNames) {
  state.errorFieldNames = new Set(fieldNames);
}

/**
 * Marks that the user clicked Submit and validation failed — distinct from `formStarted`
 * (`recordFieldInteraction`, focus-driven), since a submit can be attempted with zero fields ever
 * focused (e.g. prefilled fields left untouched, then Submit clicked immediately). Lets
 * trackNotifyModalClosed treat a failed submit as the start of an abandonable session even when no
 * field was individually interacted with.
 */
export function markSubmitAttempted() {
  state.submitAttempted = true;
}

export function markSubmitted() {
  state.submitted = true;
}

export function getElapsedSeconds() {
  if (!state.openedAt) {
    return 0;
  }

  return Math.round((Date.now() - state.openedAt) / 1000);
}

/**
 * Counts how many of the tracked fields currently have a value, reading live from the DOM
 * (form.elements) rather than from state, so it reflects the form at the exact moment of close.
 * @param {HTMLFormElement|null} formElement
 * @returns {number}
 */
export function getFieldsCompletedCount(formElement) {
  if (!formElement) {
    return 0;
  }

  return TRACKED_FIELD_NAMES.filter((fieldName) => Boolean(formElement.elements.namedItem(fieldName)?.value)).length;
}

export function getNotifySessionSnapshot() {
  return { ...state, errorFieldNames: new Set(state.errorFieldNames) };
}

export function resetNotifySession() {
  state.started = false;
  state.fromPage = null;
  state.openedAt = null;
  state.formStarted = false;
  state.submitAttempted = false;
  state.lastFieldInteracted = null;
  state.submitted = false;
  state.errorFieldNames = new Set();
}
